// 🏷️ B275 (AI_120) — Selos do Resumo Executivo: GRAVIDADE × URGÊNCIA.
// Antes havia um selo único (statusGeral) com gravidade em precedência, que
// superestimava a urgência num caso grave porém compensado/estável. Agora
// mostramos os DOIS eixos lado a lado. Fallback p/ análises antigas (salvas
// só com statusGeral) → exibe o selo combinado, sem quebrar nada.
import React from "react";
import { CircleDot } from "lucide-react";
import { theme } from "../../../shared/styles/theme";
import {
  StatusGeral,
  Gravidade,
  Urgencia,
  Adequabilidade,
} from "../services/derivarStatusGeral";

type Tom = "verde" | "amarelo" | "vermelho";

const CORES: Record<Tom, { bg: string; fg: string }> = {
  verde: { bg: "#d1fae5", fg: "#059669" },
  amarelo: { bg: "#fef3c7", fg: "#d97706" },
  vermelho: { bg: "#fee2e2", fg: "#dc2626" },
};

const GRAVIDADE_INFO: Record<Gravidade, { label: string; tom: Tom }> = {
  leve: { label: "LEVE", tom: "verde" },
  moderada: { label: "MODERADA", tom: "amarelo" },
  grave: { label: "GRAVE", tom: "vermelho" },
};

const URGENCIA_INFO: Record<Urgencia, { label: string; tom: Tom }> = {
  baixa: { label: "BAIXA", tom: "verde" },
  media: { label: "MÉDIA", tom: "amarelo" },
  alta: { label: "ALTA", tom: "vermelho" },
};

// 🫀 B351 passo 5b (AI_153) — selo da modalidade DOADOR. Substitui (não soma a)
// Gravidade × Urgência: um doador saudável não tem gravidade a declarar, e
// "urgência" no prontuário dele seria a de outra pessoa.
// INDETERMINADA é AMARELA, não cinza: falta de dado em pilar é um alerta, não um
// estado neutro — é a "regra do pilar ausente" aparecendo na tela.
const ADEQUABILIDADE_INFO: Record<Adequabilidade, { label: string; tom: Tom }> = {
  favoravel: { label: "FAVORÁVEL", tom: "verde" },
  "com-ressalvas": { label: "COM RESSALVAS", tom: "amarelo" },
  desfavoravel: { label: "DESFAVORÁVEL", tom: "vermelho" },
  indeterminada: { label: "INDETERMINADA", tom: "amarelo" },
};

const STATUS_INFO: Record<StatusGeral, { label: string; tom: Tom }> = {
  estavel: { label: "ESTÁVEL", tom: "verde" },
  atencao: { label: "ATENÇÃO", tom: "amarelo" },
  critico: { label: "CRÍTICO", tom: "vermelho" },
};

function Pilula({ texto, tom }: { texto: string; tom: Tom }) {
  const c = CORES[tom];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        padding: "6px 14px",
        borderRadius: "12px",
        fontSize: "14px",
        fontWeight: 600,
        fontFamily: theme.fonts.sans,
        background: c.bg,
        color: c.fg,
        whiteSpace: "nowrap",
      }}
    >
      <CircleDot size={14} /> {texto}
    </span>
  );
}

interface SelosStatusProps {
  gravidade?: Gravidade | null;
  urgencia?: Urgencia | null;
  /** B351 — eixo do DOADOR; quando presente, substitui gravidade/urgência. */
  adequabilidade?: Adequabilidade | null;
  /** Fallback p/ análises antigas (sem gravidade/urgência separadas). */
  statusGeral?: StatusGeral;
  /** Ajustes do container (margens etc.). */
  style?: React.CSSProperties;
}

export default function SelosStatus({
  gravidade,
  urgencia,
  adequabilidade,
  statusGeral,
  style,
}: SelosStatusProps) {
  const temEixos = Boolean(gravidade || urgencia);

  return (
    <div
      style={{
        display: "inline-flex",
        flexWrap: "wrap",
        gap: "8px",
        ...style,
      }}
    >
      {adequabilidade ? (
        <Pilula
          texto={`Adequabilidade: ${ADEQUABILIDADE_INFO[adequabilidade].label}`}
          tom={ADEQUABILIDADE_INFO[adequabilidade].tom}
        />
      ) : temEixos ? (
        <>
          {gravidade && (
            <Pilula
              texto={`Gravidade: ${GRAVIDADE_INFO[gravidade].label}`}
              tom={GRAVIDADE_INFO[gravidade].tom}
            />
          )}
          {urgencia && (
            <Pilula
              texto={`Urgência: ${URGENCIA_INFO[urgencia].label}`}
              tom={URGENCIA_INFO[urgencia].tom}
            />
          )}
        </>
      ) : (
        statusGeral && (
          <Pilula
            texto={STATUS_INFO[statusGeral].label}
            tom={STATUS_INFO[statusGeral].tom}
          />
        )
      )}
    </div>
  );
}
